/**
 * Pourquoi la capture micro est hors de portée du navigateur courant.
 *
 * `insecure-context` est le cas de la coque iPad en développement : WebKit ne
 * publie `navigator.mediaDevices` que sur une origine sûre (https, ou
 * localhost). Servie en http sur une IP du réseau local, l'API n'existe pas du
 * tout et `getUserMedia` échoue avant même d'atteindre la couche iOS, sans
 * qu'aucune demande d'autorisation ne s'affiche. C'est indiscernable, pour
 * l'utilisateur, d'un micro cassé : d'où le message explicite.
 */
export type MicUnsupportedReason = "insecure-context" | "unsupported";

/**
 * Sonde la disponibilité de la capture audio. Renvoie `null` quand
 * `getUserMedia` est appelable, sinon la raison du blocage.
 *
 * À n'appeler que côté client : `window` et `navigator` n'existent pas au
 * rendu serveur.
 */
export function probeMicCapture(): MicUnsupportedReason | null {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "unsupported";
  }

  if (typeof navigator.mediaDevices?.getUserMedia !== "function") {
    return window.isSecureContext === false ? "insecure-context" : "unsupported";
  }

  return null;
}

/** Message utilisateur pour un blocage détecté par {@link probeMicCapture}. */
export function describeMicUnsupported(reason: MicUnsupportedReason): string {
  if (reason === "insecure-context") {
    return "Le micro exige une connexion sécurisée. Cette page est servie en http, le navigateur n'expose donc aucune entrée audio.";
  }

  return "Ce navigateur n'expose pas la capture audio.";
}

/**
 * Message utilisateur pour un rejet de `getUserMedia`. Les noms sont ceux de
 * la spec Media Capture, iOS comme les navigateurs de bureau les respectent.
 */
export function describeMicError(err: unknown): string {
  const name = err instanceof Error ? err.name : "";

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Accès au micro refusé. Autorise Virtuosa dans les réglages de l'appareil, puis réessaie.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "Aucun micro détecté sur cet appareil.";
    case "NotReadableError":
    case "TrackStartError":
      return "Le micro est occupé par une autre application.";
    case "OverconstrainedError":
      return "Aucun micro ne correspond aux réglages demandés.";
    case "SecurityError":
      return describeMicUnsupported("insecure-context");
    default:
      return "Le micro n'a pas pu démarrer.";
  }
}
